// Sharing a file or folder (docs/areas/files.md): its live links with what
// each has done (opens, downloads, files in) and Revoke, and a new one: a link
// (password, an end date, a download limit, view-only) or, for a folder, a
// file request (upload-only, its own limits). The secret is made and hashed on
// this device like every link's and shown once; a password is hashed by the
// server and only the hash is kept.

import { useState } from 'react'
import { Check, Copy, Link2, Upload } from 'lucide-react'
import { engine, mutate } from '@/runtime'
import { useGrants } from '@/db/hooks'
import { hashSecret, isLive, newSecret } from '@/kinds/grant'
import type { FileResource } from '@/kinds/file/apply'
import { filesApi } from '@/lib/files/api'
import { describeShare, shareProblems, shareSpec, sharesOf, type ShareOptions } from '@/lib/files/share'
import { todayLocal } from '@/lib/time'
import { Button, Field, Segmented, Sheet } from '@/components/ui'
import { Phrases } from '@/components/Phrases'
import { SwitchRow } from '@/components/Switch'

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

export const ShareFileSheet = ({ item, onClose }: { item: FileResource; onClose: () => void }) => {
  const grants = useGrants()
  const shares = sharesOf(grants, item.id).filter((g) => isLive(g).ok)
  const [mode, setMode] = useState<'link' | 'request'>('link')
  const [name, setName] = useState(`${item.spec.name} link`)
  const [password, setPassword] = useState('')
  const [until, setUntil] = useState('')
  const [downloads, setDownloads] = useState('')
  const [viewOnly, setViewOnly] = useState(false)
  const [maxMB, setMaxMB] = useState('')
  const [maxFiles, setMaxFiles] = useState('')
  const [intro, setIntro] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [made, setMade] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [later, setLater] = useState(false)
  const num = (s: string) => (s.trim() ? Number(s) : undefined)

  const make = async () => {
    setError(null)
    const o: ShareOptions = {
      mode,
      name,
      ...(until ? { until } : {}),
      ...(mode === 'link'
        ? { maxDownloads: num(downloads), viewOnly }
        : { requestMaxMB: num(maxMB), requestMaxFiles: num(maxFiles), intro }),
    }
    const problems = shareProblems(o, todayLocal())
    if (problems.length) return setError(problems.join('. '))
    setBusy(true)
    try {
      const secret = newSecret()
      const hash = await hashSecret(secret)
      const pw = mode === 'link' && password ? (await filesApi.password(password)).hash : undefined
      await mutate.create('Grant', shareSpec(item.id, o, hash, pw), {})
      // the link opens only once the server has its grant: send it now rather than on the next sync
      await engine.sync().catch(() => setLater(true))
      setMade(`${window.location.origin}/s/${secret}`)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const copy = async () => {
    if (!made) return
    try {
      await navigator.clipboard.writeText(made)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setError('Could not copy; select the link instead')
    }
  }

  if (made)
    return (
      <Sheet open onClose={onClose} title="Ready to share">
        <div data-testid="files-share-made">
          <p className="text-sm text-muted mb-3">
            This link is shown once. Copy it now; Revoke ends it any time.
            {password && ' Send the password another way.'}
            {later && ' It starts working once this device is back online.'}
          </p>
          <Field label="Link">
            <div className="flex gap-2">
              <input
                readOnly
                value={made}
                className="w-full font-mono text-sm"
                data-testid="files-share-url"
              />
              <Button onClick={() => void copy()}>{copied ? <Check size={16} /> : <Copy size={16} />}</Button>
            </div>
          </Field>
          <div className="flex justify-end mt-3">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </Sheet>
    )

  return (
    <Sheet open onClose={onClose} title={`Share “${item.spec.name}”`}>
      {shares.length > 0 && (
        <>
          <h3 className="text-[13px] font-semibold text-muted mb-1.5">Open links</h3>
          <ul className="group-list mb-4" data-testid="files-shares">
            {shares.map((g) => (
              <li key={g.id} className="px-3.5 py-2.5 flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] truncate">{g.spec.name}</span>
                  <Phrases className="text-xs text-faint" parts={describeShare(g, fmtDate)} />
                </span>
                <Button
                  variant="chip"
                  onClick={() => void mutate.action(g.id, 'revoke')}
                  data-testid="files-share-revoke"
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void make()
        }}
        data-testid="files-share-form"
      >
        {item.spec.folder && (
          <div className="mb-3">
            <Segmented
              label="What kind of link"
              value={mode}
              onChange={(m) => {
                setMode(m)
                setName(m === 'request' ? `Send files to ${item.spec.name}` : `${item.spec.name} link`)
              }}
              options={[
                { value: 'link', label: 'A link to it' },
                { value: 'request', label: 'A file request' },
              ]}
            />
          </div>
        )}
        <p className="text-sm text-muted mb-3 flex gap-2">
          {mode === 'link' ? (
            <Link2 size={16} className="mt-0.5 shrink-0" aria-hidden />
          ) : (
            <Upload size={16} className="mt-0.5 shrink-0" aria-hidden />
          )}
          {mode === 'link'
            ? item.spec.folder
              ? 'Anyone with the link sees this folder and what is under it, and nothing else of yours.'
              : 'Anyone with the link sees this file, and nothing else of yours.'
            : 'They can send files into this folder. They never see what is in it.'}
        </p>
        <Field label="Name" hint="For you: who or what this is for. They do not see it.">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full"
            data-testid="files-share-name"
          />
        </Field>
        {mode === 'link' ? (
          <>
            <Field label="Password" hint="Optional. Wrong guesses are slowed down and recorded.">
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                data-testid="files-share-password"
              />
            </Field>
            <Field label="Downloads before it ends" hint="Optional. Empty is no limit.">
              <input
                inputMode="numeric"
                value={downloads}
                onChange={(e) => setDownloads(e.target.value.replace(/\D/g, ''))}
                className="w-full"
                data-testid="files-share-downloads"
              />
            </Field>
            <SwitchRow
              className="mb-3"
              label="View only"
              hint="Hides Download. Someone set on keeping it can still save what they see."
              on={viewOnly}
              onChange={setViewOnly}
              testId="files-share-viewonly"
            />
          </>
        ) : (
          <>
            <Field label="A line for them" hint="Optional, e.g. “Receipts for October, please”.">
              <input
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                className="w-full"
                maxLength={300}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Largest file (MB)" hint="1024 unless set">
                <input
                  inputMode="numeric"
                  value={maxMB}
                  onChange={(e) => setMaxMB(e.target.value.replace(/\D/g, ''))}
                  className="w-full"
                />
              </Field>
              <Field label="Files in all" hint="No limit unless set">
                <input
                  inputMode="numeric"
                  value={maxFiles}
                  onChange={(e) => setMaxFiles(e.target.value.replace(/\D/g, ''))}
                  className="w-full"
                />
              </Field>
            </div>
          </>
        )}
        <Field label="Works until" hint="Optional. The end of that day.">
          <input
            type="date"
            value={until}
            min={todayLocal()}
            onChange={(e) => setUntil(e.target.value)}
            className="w-full"
            data-testid="files-share-until"
          />
        </Field>
        {error && <p className="text-sm text-danger mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={busy} data-testid="files-share-make">
            {busy ? 'Making…' : 'Make the link'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
